// Toast notification component - Signal-based snackbar
// Used for non-intrusive feedback (e.g., locked cell click)

import { signal } from '@preact/signals'
import { useEffect } from 'preact/hooks'

// Toast state signal
const toastState = signal(null) // { message, actionLabel, actionHref }
let dismissTimer = null

/**
 * Show a toast notification
 * @param {string} message - Main message text
 * @param {string} [actionLabel] - Optional action button text
 * @param {string} [actionHref] - Optional action URL
 * @param {number} [duration=4000] - Auto-dismiss duration in ms
 */
export function showToast(message, actionLabel, actionHref, duration = 4000) {
  clearTimeout(dismissTimer)
  toastState.value = { message, actionLabel, actionHref }
  dismissTimer = setTimeout(() => {
    toastState.value = null
  }, duration)
}

export function dismissToast() {
  clearTimeout(dismissTimer)
  toastState.value = null
}

/**
 * Toast container component - render once in app root
 */
export default function Toast() {
  const toast = toastState.value

  // Clean up timer on unmount
  useEffect(() => {
    return () => clearTimeout(dismissTimer)
  }, [])

  if (!toast) return null

  return (
    <div className="toast-container" onClick={dismissToast}>
      <div className="toast" onClick={(e) => e.stopPropagation()}>
        <span className="toast__message">{toast.message}</span>
        {toast.actionLabel && toast.actionHref && (
          <a
            className="toast__action"
            href={toast.actionHref}
            onClick={dismissToast}
          >
            {toast.actionLabel}
          </a>
        )}
        <button className="toast__dismiss" onClick={dismissToast} aria-label="Dismiss">
          ×
        </button>
      </div>
    </div>
  )
}
